import { useMutation, useQuery } from "@apollo/client";
import { Form, Input, Checkbox, Select, message } from "antd";
import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-number-input";
import { isUndefined } from "lodash";
import "react-phone-number-input/style.css";
import { DEFAULT_COUNTRY } from "../../../config";
import { ADD_USER, UPDATE_USER } from "../../../graphql/mutation";
import { GET_DEPARTMENTS } from "../../../graphql/query";
import useLang from "../../../hooks/useLang";
import { colorConfig } from "../../../library/colorConfig";
import { closeDialog } from "../../../library/helpers";
import CustomSwitch from "../../commonComponents/switch";
import { MFA_METHODS } from "../../../constant/LocationConstant";
import { parseUserSettings } from "../../../utils/userHelpers";

const parsePermissions = (role, userLang) => {
  const permissions = {
    canCreateUsers: userLang.usersPermission,
    canCreateTeams: userLang.teamsPermission,
    canViewProspects: userLang.prospectsPermission,
    isCompanyAdmin: userLang.companyAdminPermission,
  };
  let permissionString = "";
  Object.entries(role).map(([p, val]) => {
    permissionString += permissions[p] && val === true ? `${permissions[p]}, ` : "";
  });
  return permissionString
    ? permissionString.substring(0, permissionString.length - 2)
    : userLang.noPermissions;
};

const UserForm = ({
  company,
  rolesData,
  locationData,
  companyRoles,
  mode,
  user,
  refetchUsers,
  onClose,
  isSubmit,
  setIsSubmit,
  submitAction,
  refreshUsers,
  locationId = null,
  refetchCompanyRoles,
}) => {
  const [form] = Form.useForm();
  const [lang] = useLang();
  const [userLang] = useLang("UserPage");
  const [enableMFA, setEnableMFA] = useState(false);
  const [mfaMethods, setMFAMethods] = useState([]);

  // Watch form fields for real-time values
  const selectedLocationId = Form.useWatch("locationId", form);
  const selectedCompanyRoleId = Form.useWatch("companyRoleId", form);

  // Build location options with placeholder for selected value while loading
  const getLocationOptions = () => {
    const options = locationData || [];
    const formLocationValue = form.getFieldValue("locationId");

    // Check if we have a selected value that's not in the loaded options
    const selectedValue = formLocationValue || locationId;
    if (selectedValue) {
      const valueExists = options.find(loc => +loc.id === +selectedValue);
      if (!valueExists) {
        // Add placeholder option for the selected value
        return [{ id: selectedValue, title: "Loading..." }, ...options];
      }
    }

    return options;
  };

  // Fetch departments based on selected location
  const { data: departmentsData, loading: departmentsLoading } = useQuery(GET_DEPARTMENTS, {
    variables: { locationId: selectedLocationId },
    skip: isUndefined(selectedLocationId) || !selectedLocationId,
  });

  useEffect(() => {
    console.log("useEffect triggered - mode:", mode, "locationId:", locationId);

    if (mode === "EDIT" && user) {
      console.log("EDIT mode - setting user fields");
      // Parse user settings (can be string or object)
      const settings = parseUserSettings(user?.settings);

      // Set MFA state from settings
      setEnableMFA(settings?.MFA_ENABLED || false);
      setMFAMethods(settings?.MFA_METHODS || []);

      form.setFieldsValue({
        ...user,
        roleId: user?.role?.id || null,
        departmentIds: user?.departmentIds || [],
        companyRoleId: user?.companyRoleId || null,
        enableMFA: settings?.MFA_ENABLED || false,
        preferredName: settings?.preferredName || "",
      });
    } else {
      console.log("CREATE mode - resetting form and setting locationId:", locationId);
      // In CREATE mode, reset form and set locationId
      form.resetFields();
      setEnableMFA(false);
      setMFAMethods([]);

      // Set locationId after reset to ensure it's applied
      if (locationId) {
        console.log("Setting locationId to:", locationId);
        // Use setTimeout to ensure this runs after resetFields completes
        setTimeout(() => {
          form.setFieldsValue({
            locationId: locationId,
          });
          console.log("Form locationId field set to:", form.getFieldValue("locationId"));
        }, 0);
      } else {
        console.log("No locationId to set!");
      }
    }
  }, [mode, user, form, locationId]);

  useEffect(() => {
    // Only update the enableMFA field, don't reset other fields
    form.setFieldsValue({
      enableMFA: enableMFA,
    });
  }, [enableMFA]);

  // Handle form submission when isSubmit changes
  useEffect(() => {
    if (isSubmit) {
      form.submit();
    }
  }, [isSubmit, form]);

  const [updateUser] = useMutation(UPDATE_USER);
  const [createUser] = useMutation(ADD_USER);

  const close = () => {
    if (onClose) {
      onClose();
    } else {
      closeDialog("userForm");
    }
  };

  const submitUser = async values => {
    try {
      if (mode === "EDIT") {
        const res = await updateUser({
          variables: {
            ...values,
            userId: user.id,
            companyId: company.id,
            settings: {
              MFA_ENABLED: enableMFA,
              MFA_METHODS: enableMFA ? mfaMethods : [],
              preferredName: values.preferredName || "",
            },
          },
        });
        if (res.data.updateUser.statusCode === 200) {
          message.success(res.data.updateUser.message);
          refetchUsers && refetchUsers();
          close();
        } else {
          message.error(res.data.updateUser.message);
        }
      } else {
        const res = await createUser({
          variables: {
            ...values,
            companyId: company.id,
            settings: {
              MFA_ENABLED: enableMFA,
              MFA_METHODS: enableMFA ? mfaMethods : [],
              preferredName: values.preferredName || "",
            },
          },
        });
        if (res.data.createUser.statusCode === 200) {
          refetchUsers && refetchUsers();
          refreshUsers && refreshUsers();

          // Handle different submit actions
          if (submitAction === "saveAndAddAnother") {
            // Reset form fields but keep drawer open
            form.resetFields();
            setEnableMFA(false);
            setMFAMethods([]);
            message.success(userLang.userCreatedAddAnother);
          } else if (submitAction === "saveUser") {
            // Close the drawer
            close();
            message.success(userLang.userCreatedSuccessfully);
          } else {
            // Default behavior (existing functionality)
            close();
          }
        } else {
          message.error(res.data.createUser.message);
        }
      }
    } catch (error) {
      message.error(userLang.errorSavingUser);
      console.error("Submit user error:", error);
    } finally {
      // Reset submit state
      setIsSubmit && setIsSubmit(false);
    }
  };

  const { Option } = Select;

  const handleFormValidationFailed = errorInfo => {
    // Reset submit state when validation fails
    setIsSubmit && setIsSubmit(false);
  };

  return (
    <Form
      form={form}
      onFinish={submitUser}
      onFinishFailed={handleFormValidationFailed}
      layout="vertical"
      className="w-full"
      initialValues={{
        locationId: mode === "CREATE" ? locationId : undefined,
      }}
    >
      <Form.Item
        name="firstName"
        rules={[{ required: true, message: userLang.pleaseEnterFirstName }]}
        label={
          <span className="text-black/[.88] text-sm font-normal">{userLang.firstNameLabel}</span>
        }
      >
        <Input placeholder={lang.firstName} name="firstName" />
      </Form.Item>
      <Form.Item
        name="lastName"
        rules={[{ required: true, message: userLang.pleaseEnterLastName }]}
        label={
          <span className="text-black/[.88] text-sm font-normal">{userLang.lastNameLabel}</span>
        }
      >
        <Input placeholder={lang.lastName} name="lastName" />
      </Form.Item>
      <Form.Item
        name="preferredName"
        label={
          <span className="text-black/[.88] text-sm font-normal">
            {userLang.preferredName}{" "}
            <span className="text-black/[0.45] text-sm font-normal">{userLang.optional}</span>
          </span>
        }
      >
        <Input placeholder={userLang.preferredName} name="preferredName" />
      </Form.Item>
      <p className="text-black/[0.45] text-sm font-normal -mt-2 mb-4">
        {userLang.preferredNameHelp}
      </p>
      <Form.Item
        name="email"
        rules={[
          { required: true, message: userLang.emailRequired },
          { type: "email", message: userLang.pleaseEnterValidEmail },
        ]}
        label={
          <span className="text-black/[.88] text-sm font-normal">{userLang.emailAddressLabel}</span>
        }
      >
        <Input placeholder={lang.emailLabel} name="email" />
      </Form.Item>
      <Form.Item
        label={<span className="text-black/[.88] text-sm font-normal">{lang.selectLocation}</span>}
        name="locationId"
        rules={[{ required: true, message: userLang.pleaseSelectLocation }]}
      >
        <Select
          className="w-full"
          placeholder={lang.selectLocation}
          showArrow
          name="locationId"
          onChange={value => {
            // Reset company role and department fields when location changes
            form.setFieldsValue({ companyRoleId: null, departmentIds: [] });
            if (value && refetchCompanyRoles) {
              refetchCompanyRoles([+value]);
            }
          }}
        >
          {getLocationOptions().map(item => (
            <Option key={item.id} value={+item.id}>
              {item.title}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="phone"
        rules={[{ required: true, message: userLang.pleaseEnterPhoneNumber }]}
        label={
          <span className="text-black/[.88] text-sm font-normal">{userLang.phoneNumberLabel}</span>
        }
      >
        <PhoneInput
          name="phone"
          international
          withCountryCallingCode
          countryCallingCodeEditable={false}
          defaultCountry={DEFAULT_COUNTRY}
          placeholder={lang.phoneNumber}
          label={lang.phoneNumber}
          className="phoneInputCss py-1 w-full"
        />
      </Form.Item>

      <Form.Item
        name="roleId"
        label={<span className="text-black/[.88] text-sm font-normal">{lang.selectRole}</span>}
      >
        <Select
          className="w-full"
          name="roleId"
          placeholder={lang.selectRole}
          showSearch
          showArrow
          filterOption={(input, option) => {
            const role = rolesData?.roles?.find(r => r.id === option.key);
            return role?.name?.toLowerCase().includes(input.toLowerCase());
          }}
        >
          {rolesData &&
            rolesData.roles.map(role => (
              <Option key={role.id}>
                {role.name} -{" "}
                <span className={`text-[12px] text-[${colorConfig.medDarkgray}]`}>
                  {parsePermissions(role, userLang)}
                </span>
              </Option>
            ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="companyRoleId"
        label={
          <span className="text-black/[.88] text-sm font-normal">{lang.selectCompanyRole}</span>
        }
      >
        <Select
          className="w-full"
          name="companyRole"
          placeholder={lang.selectCompanyRole}
          showSearch
          filterOption={(input, option) =>
            option.children?.toLowerCase().includes(input.toLowerCase())
          }
          onChange={() => {
            // Reset department field when company role changes
            form.setFieldsValue({ departmentIds: [] });
          }}
          disabled={!selectedLocationId}
        >
          {(companyRoles || [])
            .filter(role => {
              // If no location is selected, show all roles
              if (!selectedLocationId) return true;

              // Filter roles that have the selected location
              return (
                role.locations &&
                role.locations.some(
                  loc => loc.location && String(loc.location.id) === String(selectedLocationId)
                )
              );
            })
            .map(item => (
              <Option key={item.id} value={+item.id}>
                {item.name}
              </Option>
            ))}
        </Select>
      </Form.Item>

      {/* Department dropdown - only show when location is selected */}
      {selectedLocationId && (
        <Form.Item
          label={
            <span className="text-black/[.88] text-sm font-normal">
              {userLang.selectDepartment || "Select Department"}
            </span>
          }
          name="departmentIds"
        >
          <Select
            className="w-full"
            mode="multiple"
            placeholder={userLang.selectDepartment || "Select Department"}
            showArrow
            name="departmentIds"
            allowClear
            loading={departmentsLoading}
            disabled={!selectedCompanyRoleId}
          >
            {(departmentsData?.getDepartments || []).map(department => (
              <Option key={department.id} value={+department.id}>
                {department.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <h3 className="mb-5 mt-8">{userLang.mfaSettingLabel}</h3>
      <Form.Item name="enableMFA" valuePropName="checked">
        <div>
          <CustomSwitch
            checked={enableMFA}
            onChange={() => {
              setEnableMFA(!enableMFA);
              if (!enableMFA) {
                setMFAMethods([MFA_METHODS.SOFTWARE_TOKEN_MFA]);
              } else {
                setMFAMethods([]);
              }
            }}
            size="small"
            labelPosition="post"
            label={userLang.mandateMultiFactorAuth}
            labelClass="text-black-88 text-sm font-normal"
          />
          <p className="text-black/[.65] text-sm font-normal mt-1">{userLang.mfaDescription}</p>
        </div>
      </Form.Item>
      {enableMFA && (
        <Form.Item
          className="ml-5"
          label={
            <span className="text-black/[.88] text-sm font-normal pb-0">
              {userLang.signInMethodLabel}
            </span>
          }
          name="mfaMethod"
        >
          <Checkbox
            checked={mfaMethods?.includes(MFA_METHODS.SOFTWARE_TOKEN_MFA)}
            onChange={e => {
              const updatedMethods = e.target.checked
                ? [...(mfaMethods || []), MFA_METHODS.SOFTWARE_TOKEN_MFA]
                : mfaMethods?.filter(method => method !== MFA_METHODS.SOFTWARE_TOKEN_MFA);
              setMFAMethods(updatedMethods);
            }}
          >
            <span className="text-black text-sm">{userLang.authenticatorAppLabel}</span>
          </Checkbox>
        </Form.Item>
      )}
    </Form>
  );
};

export default UserForm;
